import React, { useState, useEffect } from 'react';
import { Modal, Button, Form } from 'react-bootstrap';
import { useAuth } from '../../context/AuthContext'; 
import { formatNumber, parseLocaleNumber } from '../../utils/numberFormatter';

const AuthorizationModal = ({ show, onClose, onRequest, initialAmount }) => {
  const [amount, setAmount] = useState('');
  const [displayAmount, setDisplayAmount] = useState('');
  const [observation, setObservation] = useState('');
  const [password, setPassword] = useState('');
  const [attachment, setAttachment] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const { config } = useAuth();
  
  useEffect(() => {
    if (show) {
      if (initialAmount) {
        setAmount(initialAmount.toString());
        setDisplayAmount(formatNumber(initialAmount));
      } else {
        setAmount('');
        setDisplayAmount('');
      }
      setObservation('');
      setPassword('');
      setAttachment(null);
      setIsLoading(false);
    }
  }, [show, initialAmount]);

  const handleAmountChange = (e) => {
    const value = e.target.value.replace(/[^0-9,]/g, '');
    
    if (value === '') {
      setAmount('');
      setDisplayAmount('');
      return;
    }

    const numericValue = parseLocaleNumber(value);
    if (!isNaN(numericValue)) {
      setAmount(numericValue.toString());
      setDisplayAmount(formatNumber(numericValue));
    }
  };

  const handleFileChange = (e) => {
    setAttachment(e.target.files[0]);
  };

  const handleRequest = async () => {
    if (isLoading) return;
    
    setIsLoading(true);
    try {
      await onRequest({ amount, observation, password, attachment });
      // Solo se ejecuta si no hay error - el modal se cerrará automáticamente
    } catch (error) {
      // Si hay un error, resetear el estado de loading para permitir reintentos
      console.error('Error en la solicitud de autorización:', error);
      setIsLoading(false);
      // El error se maneja en el componente padre que llama a onRequest
    }
  };

  const shouldShowAdminAuthorizationAlert = () => {
    const amountValue = parseFloat(amount);
    const maxAmount = config.self_authorization?.max_amount;
    
    // Si no hay self_authorization configurado, siempre requerir autorización administrativa
    if (!config.self_authorization?.self_authorization) {
      return true;
    }

    // Si max_amount es null o false, significa que no hay límite
    if (maxAmount === null || maxAmount === false) {
      return false;
    }

    // Si hay un límite establecido, verificar si el monto lo excede
    return amountValue > maxAmount;
  };

  const shouldShowMerchantPassword = () => {
    const amountValue = parseFloat(amount);
    const minAmount = config.self_authorization?.min_amount_for_authentication;
    const maxAmount = config.self_authorization?.max_amount;

    // Si no hay configuración de self_authorization o min_amount_for_authentication es false, no requerir contraseña
    if (!config.self_authorization || minAmount === false) {
      return false;
    }

    // Si hay un monto mínimo establecido y un monto máximo (que no sea false)
    return (
      minAmount !== null &&
      amountValue > minAmount &&
      (maxAmount === false || maxAmount === null || amountValue <= maxAmount)
    );
  };

  return (
    <Modal show={show} onHide={onClose} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>Solicitar Autorización</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form>
          <Form.Group controlId="authorizationAmount" className="mt-1">
            <Form.Label>Monto Solicitado</Form.Label>
            <Form.Control
              type="text"
              placeholder="Ingrese el monto"
              autoComplete="off"
              value={displayAmount}
              onChange={handleAmountChange}
            />
          </Form.Group>
          <Form.Group controlId="authorizationObservation" className="mt-3">
            <Form.Label>Información Adicional</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              placeholder="Por favor, ingresa el motivo por el cual se solicita la autorización"
              value={observation}
              onChange={(e) => setObservation(e.target.value.slice(0, 255))}
              maxLength={255}
            />
            <Form.Text className="text-muted">
              Máximo 255 caracteres
            </Form.Text>
          </Form.Group>
          {shouldShowMerchantPassword() && (
            <Form.Group controlId="password" className="mt-3">
              <Form.Label>Contraseña</Form.Label>
              <Form.Control
                type="password"
                placeholder="Ingrese su contraseña"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="off"
              />
              <Form.Text className="text-muted">
                Se requiere contraseña para montos superiores a ${formatNumber(config.self_authorization?.min_amount_for_authentication)}
              </Form.Text>
            </Form.Group>
          )}
          
          <Form.Group controlId="authorizationAttachment" className="mt-3">
            <Form.Label>Archivo Adjunto (Opcional)</Form.Label>
            <div
              className={`attachment-dropzone ${attachment ? 'uploaded' : ''}`}
              onClick={() => document.getElementById('authorizationAttachment').click()}
              style={{ 
                userSelect: 'none',
                border: '2px dashed #dee2e6',
                borderRadius: '8px',
                padding: '20px',
                textAlign: 'center',
                cursor: 'pointer',
                backgroundColor: attachment ? '#f8f9fa' : 'white',
                width: '100%'
              }}
            >
              <span className="attachment-icon">
                <i className="bi bi-file-earmark-text fs-4"></i>
              </span>
              <div className="attachment-name flex-grow-1 mt-2">
                {attachment ? attachment.name : 'Arrastra el archivo aquí o haz clic para seleccionarlo'}
              </div>
              <span className="attachment-status mt-2">
                {attachment ? (
                  <i className="bi bi-check-circle-fill text-success"></i>
                ) : null }
              </span>
              <input
                type="file"
                id="authorizationAttachment"
                accept="image/*,application/pdf"
                style={{ display: 'none' }}
                onChange={handleFileChange}
              />
            </div>
            <Form.Text className="text-muted">
              Formatos aceptados: PDF, JPG, PNG
            </Form.Text>
          </Form.Group>
        </Form>

        {shouldShowAdminAuthorizationAlert() && (
          <div className="mt-4 p-3 bg-warning bg-opacity-10 border border-warning rounded">
            <span className="text-secondary">
              Esta solicitud deberá ser autorizada por un administrador.
            </span>
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onClose} disabled={isLoading}>
          Cancelar
        </Button>
        <Button 
          variant="primary" 
          onClick={handleRequest} 
          disabled={!amount || parseFloat(amount) <= 0 || observation.trim().length < 1 || isLoading}
        >
          {isLoading ? (
            <>
              <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
              Cargando...
            </>
          ) : (
            'Solicitar'
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default AuthorizationModal;
